import React from "react";
import type { IconType } from "react-icons";

/**
 * Presentation-only building blocks shared by the sign-in, registration,
 * password and guest-claim screens. Callers keep all of their own state and
 * handlers; these components only arrange what they are given.
 */

/* ---------- Page frame: brand panel + form column ---------- */
interface AuthLayoutProps {
  aside?: React.ReactNode;
  children: React.ReactNode;
}

export const AuthLayout = ({ aside, children }: AuthLayoutProps) => (
  <div className="w-full px-4 py-6 sm:px-6 sm:py-10 lg:py-12">
    <div className="mx-auto grid w-full max-w-6xl gap-6 lg:grid-cols-2 lg:items-stretch lg:gap-10 xl:gap-14">
      {aside}
      <div className="flex items-start justify-center lg:items-center">{children}</div>
    </div>
  </div>
);

/* ---------- Form card ---------- */
interface AuthCardProps {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  icon?: IconType;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
}

export const AuthCard = ({ eyebrow, title, subtitle, icon: Icon, children, footer, className = "" }: AuthCardProps) => (
  <section className={`zs-card w-full max-w-[460px] animate-fadeInUp ${className}`}>
    <div className="px-6 pt-7 pb-6 sm:px-9 sm:pt-9">
      {Icon && (
        <span className="mb-5 inline-flex h-11 w-11 items-center justify-center rounded-lg bg-primary-soft text-primary-text">
          <Icon className="h-5 w-5" aria-hidden="true" />
        </span>
      )}
      {eyebrow && <p className="zs-eyebrow">{eyebrow}</p>}
      <h2 className="font-display text-2xl font-bold leading-tight tracking-tight text-ink">{title}</h2>
      {subtitle && <p className="mt-2 text-sm leading-relaxed text-ink-3">{subtitle}</p>}
      <div className="mt-7">{children}</div>
    </div>
    {footer && (
      <div className="rounded-b-lg border-t border-line bg-surface-2 px-6 py-4 text-center text-sm text-ink-3 sm:px-9">
        {footer}
      </div>
    )}
  </section>
);

/* ---------- "or" divider between social and email sign-in ---------- */
export const AuthDivider = ({ label }: { label: React.ReactNode }) => (
  <div className="my-6 flex items-center gap-3">
    <span className="zs-divider flex-1" />
    <span className="text-xs font-medium text-ink-4">{label}</span>
    <span className="zs-divider flex-1" />
  </div>
);

/* ---------- Brand panel ---------- */
export interface BrandFeature {
  icon: IconType;
  title: string;
  text: string;
}

type PanelTone = "primary" | "success" | "warning";

const toneBadge: Record<PanelTone, string> = {
  primary: "bg-surface text-primary-text",
  success: "bg-success-soft text-success",
  warning: "bg-warning-soft text-warning",
};

interface BrandPanelProps {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  features?: BrandFeature[];
  image?: string;
  imageAlt?: string;
  /** Optional status mark shown above the title (survey outcome panels). */
  statusIcon?: IconType;
  tone?: PanelTone;
  /**
   * On small screens, place the panel above the form instead of below it.
   * Use when the panel carries the page's message (e.g. a survey outcome).
   * The panel is shown at every size either way, as it was before the redesign.
   */
  showOnMobile?: boolean;
}

export const BrandPanel = ({
  eyebrow,
  title,
  description,
  features = [],
  image,
  imageAlt = "",
  statusIcon: StatusIcon,
  tone = "primary",
  showOnMobile = false,
}: BrandPanelProps) => (
  <aside
    className={`${showOnMobile ? "" : "order-last lg:order-none"} flex relative w-full flex-col overflow-hidden rounded-xl border border-line bg-primary-soft px-5 py-6 sm:px-8 sm:py-8 lg:justify-between lg:p-10 xl:p-12`}
  >
    <div className="relative">
      {StatusIcon && (
        <span
          className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-full border border-line ${toneBadge[tone]}`}
        >
          <StatusIcon className="h-6 w-6" aria-hidden="true" />
        </span>
      )}
      {eyebrow && <p className="zs-eyebrow">{eyebrow}</p>}
      <h2 className="max-w-[18ch] font-display text-2xl font-bold leading-tight tracking-tight text-ink sm:text-3xl xl:text-[2.5rem] xl:leading-[1.1]">
        {title}
      </h2>
      {description && (
        <p className="mt-3 max-w-[46ch] text-sm leading-relaxed text-ink-2 sm:text-base">{description}</p>
      )}

      {features.length > 0 && (
        <ul className="grid gap-5 mt-9">
          {features.map(({ icon: Icon, title: featureTitle, text }) => (
            <li key={featureTitle} className="flex items-start gap-3.5">
              <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-line bg-surface text-primary-text shadow-xs">
                <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
              </span>
              <div className="min-w-0 pt-0.5">
                <p className="text-sm font-semibold text-ink">{featureTitle}</p>
                <p className="mt-0.5 text-sm leading-relaxed text-ink-3">{text}</p>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>

    {image && (
      <div className="relative flex justify-center mt-10">
        <img src={image} alt={imageAlt} className="max-h-64 w-auto max-w-full rounded-lg object-contain animate-slideInUp" />
      </div>
    )}
  </aside>
);
